// ═══════════════════════════════════════════════════════════════
//  DOCUMENT THEMES — shared by quotations and invoices
// ═══════════════════════════════════════════════════════════════
// One catalogue, one set of tokens. A PDF builder reads the tokens rather
// than hard-coding colours and rules, so adding a theme here makes it
// available to every document that renders through those builders.
//
// Colours are [r, g, b] because that is what jsPDF takes.

export const DOC_THEMES = [
  {
    key: 'classic',
    label: 'Classic',
    description: 'Centred masthead with a gold rule. The original Wedoo layout.',
    accent: [245, 168, 0],
    ink: [20, 20, 20],
    muted: [110, 110, 110],
    band: [255, 244, 222],      // category / section row fill
    bandInk: [120, 85, 0],
    panel: [248, 246, 240],     // client details panel
    tableHead: [20, 20, 20],
    tableHeadInk: [255, 255, 255],
    zebra: null,                // no alternating rows
    header: 'centered',         // centered | band | rule
    swatch: '#F5A800',
  },
  {
    key: 'modern',
    label: 'Modern',
    description: 'Solid accent header band, zebra rows, totals in a tinted block.',
    accent: [245, 168, 0],
    ink: [17, 24, 39],
    muted: [107, 114, 128],
    band: [255, 248, 235],
    bandInk: [146, 100, 0],
    panel: [249, 250, 251],
    tableHead: [245, 168, 0],
    tableHeadInk: [30, 20, 0],
    zebra: [250, 250, 249],
    header: 'band',
    swatch: '#F5A800',
  },
  {
    key: 'minimal',
    label: 'Minimal',
    description: 'Rules and type only. No colour ink, photocopies cleanly.',
    accent: [60, 60, 60],
    ink: [17, 17, 17],
    muted: [120, 120, 120],
    band: [242, 242, 242],
    bandInk: [40, 40, 40],
    panel: [248, 248, 248],
    tableHead: [40, 40, 40],
    tableHeadInk: [255, 255, 255],
    zebra: null,
    header: 'rule',
    swatch: '#3C3C3C',
  },
  {
    key: 'elegant',
    label: 'Elegant',
    description: 'Deep charcoal header with gold detailing, for premium proposals.',
    accent: [196, 134, 0],
    ink: [26, 24, 20],
    muted: [125, 118, 105],
    band: [250, 245, 232],
    bandInk: [120, 85, 0],
    panel: [252, 250, 245],
    tableHead: [26, 24, 20],
    tableHeadInk: [245, 222, 170],
    zebra: [252, 251, 248],
    header: 'band',
    swatch: '#1A1814',
  },
  {
    key: 'corporate',
    label: 'Corporate',
    description: 'Navy header band with a crisp white table head. Reads as formal.',
    accent: [30, 58, 95],
    ink: [17, 24, 39],
    muted: [100, 116, 139],
    band: [238, 242, 248],
    bandInk: [30, 58, 95],
    panel: [247, 249, 252],
    tableHead: [30, 58, 95],
    tableHeadInk: [255, 255, 255],
    zebra: [248, 250, 252],
    header: 'band',
    swatch: '#1E3A5F',
  },
  {
    key: 'slate',
    label: 'Slate',
    description: 'Cool grey rule header with zebra rows. Quiet and neutral.',
    accent: [71, 85, 105],
    ink: [30, 41, 59],
    muted: [113, 128, 150],
    band: [241, 245, 249],
    bandInk: [51, 65, 85],
    panel: [248, 250, 252],
    tableHead: [71, 85, 105],
    tableHeadInk: [255, 255, 255],
    zebra: [248, 250, 252],
    header: 'rule',
    swatch: '#475569',
  },
  {
    key: 'noir',
    label: 'Noir',
    description: 'Full black masthead, stark white type. High contrast.',
    accent: [17, 17, 17],
    ink: [17, 17, 17],
    muted: [115, 115, 115],
    band: [235, 235, 235],
    bandInk: [17, 17, 17],
    panel: [246, 246, 246],
    tableHead: [17, 17, 17],
    tableHeadInk: [255, 255, 255],
    zebra: [250, 250, 250],
    header: 'split',
    swatch: '#111111',
  },
  {
    key: 'blueprint',
    label: 'Blueprint',
    description: 'Technical drawing feel — thin blue rules, boxed masthead.',
    accent: [37, 99, 235],
    ink: [30, 41, 59],
    muted: [100, 116, 139],
    band: [239, 246, 255],
    bandInk: [30, 64, 175],
    panel: [248, 251, 255],
    tableHead: [37, 99, 235],
    tableHeadInk: [255, 255, 255],
    zebra: null,
    header: 'boxed',
    swatch: '#2563EB',
  },
  {
    key: 'terracotta',
    label: 'Terracotta',
    description: 'Warm clay tones on cream — suits interior proposals.',
    accent: [180, 83, 47],
    ink: [60, 40, 32],
    muted: [140, 115, 100],
    band: [252, 240, 232],
    bandInk: [150, 66, 36],
    panel: [253, 248, 244],
    tableHead: [180, 83, 47],
    tableHeadInk: [255, 255, 255],
    zebra: [253, 248, 245],
    header: 'stripe',
    swatch: '#B4532F',
  },
  {
    key: 'forest',
    label: 'Forest',
    description: 'Deep green rail down the page edge. Calm and natural.',
    accent: [22, 101, 74],
    ink: [20, 40, 33],
    muted: [107, 128, 120],
    band: [233, 246, 241],
    bandInk: [17, 84, 61],
    panel: [246, 252, 249],
    tableHead: [22, 101, 74],
    tableHeadInk: [255, 255, 255],
    zebra: [247, 252, 250],
    header: 'rail',
    swatch: '#16654A',
  },
  {
    key: 'royal',
    label: 'Royal',
    description: 'Plum header with gold rules, for high-value proposals.',
    accent: [88, 28, 96],
    ink: [40, 20, 45],
    muted: [130, 110, 135],
    band: [246, 238, 248],
    bandInk: [88, 28, 96],
    panel: [251, 247, 252],
    tableHead: [88, 28, 96],
    tableHeadInk: [245, 222, 170],
    zebra: [251, 248, 252],
    header: 'band',
    swatch: '#581C60',
  },
  {
    key: 'sand',
    label: 'Sand',
    description: 'Soft beige panels with a thin bronze stripe. Understated.',
    accent: [166, 124, 82],
    ink: [61, 51, 42],
    muted: [146, 130, 112],
    band: [246, 239, 229],
    bandInk: [125, 89, 52],
    panel: [250, 246, 239],
    tableHead: [166, 124, 82],
    tableHeadInk: [255, 255, 255],
    zebra: [251, 248, 243],
    header: 'stripe',
    swatch: '#A67C52',
  },
  {
    key: 'monoline',
    label: 'Monoline',
    description: 'Outlined boxes, no fills at all. Very light on ink.',
    accent: [80, 80, 80],
    ink: [26, 26, 26],
    muted: [125, 125, 125],
    band: [255, 255, 255],
    bandInk: [40, 40, 40],
    panel: [255, 255, 255],
    tableHead: [255, 255, 255],
    tableHeadInk: [26, 26, 26],
    zebra: null,
    header: 'boxed',
    swatch: '#505050',
  },
  {
    key: 'teal',
    label: 'Teal',
    description: 'Teal rail and table head. Fresh without being loud.',
    accent: [15, 118, 130],
    ink: [17, 40, 45],
    muted: [100, 125, 130],
    band: [232, 247, 249],
    bandInk: [12, 94, 105],
    panel: [245, 252, 253],
    tableHead: [15, 118, 130],
    tableHeadInk: [255, 255, 255],
    zebra: [247, 253, 253],
    header: 'rail',
    swatch: '#0F7682',
  },
];

export const DEFAULT_THEME = 'classic';

export const getTheme = (key) =>
  DOC_THEMES.find(t => t.key === key) || DOC_THEMES.find(t => t.key === DEFAULT_THEME);

// Invoices shipped with three variants before themes existed. Saved buttons and
// any stored preference still pass those names, so map them rather than break.
const LEGACY_INVOICE_VARIANTS = {
  default: 'classic',
  color:   'modern',
  emerald: 'emerald',
};

// Emerald was an invoice-only look; keep it working by describing it with the
// same tokens instead of leaving a second styling system in the codebase.
const EMERALD = {
  key: 'emerald',
  label: 'Emerald',
  description: 'Green accent header — the existing invoice variant.',
  accent: [5, 150, 105],
  ink: [17, 24, 39],
  muted: [107, 114, 128],
  band: [236, 253, 245],
  bandInk: [6, 95, 70],
  panel: [240, 253, 249],
  tableHead: [5, 150, 105],
  tableHeadInk: [255, 255, 255],
  zebra: [247, 254, 251],
  header: 'band',
  swatch: '#059669',
};

export const resolveTheme = (keyOrVariant) => {
  if (keyOrVariant === 'emerald') return EMERALD;
  const mapped = LEGACY_INVOICE_VARIANTS[keyOrVariant] || keyOrVariant;
  if (mapped === 'emerald') return EMERALD;
  return getTheme(mapped);
};

// Themes offered for invoices: the four core ones plus the legacy green.
export const INVOICE_THEMES = [...DOC_THEMES, EMERALD];

export const rgb = (c) => `rgb(${c[0]}, ${c[1]}, ${c[2]})`;

// The picked theme is a per-user preference, not document data, so it lives in
// the browser. Wrapped because storage throws in private windows.
const STORAGE_KEY = 'wedoo_doc_theme';
export const loadThemeChoice = () => {
  try { return localStorage.getItem(STORAGE_KEY) || DEFAULT_THEME; }
  catch { return DEFAULT_THEME; }
};
export const saveThemeChoice = (key) => {
  try { localStorage.setItem(STORAGE_KEY, key); } catch { /* ignore */ }
};
